import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Link,
  Spinner,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowClockwise24Regular,
  CheckmarkCircle20Filled,
  ErrorCircle20Filled,
  Warning20Filled,
  QuestionCircle20Filled,
  ChevronDown20Regular,
  ChevronRight20Regular,
} from '@fluentui/react-icons';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessDateTime } from '../lib/businessTime';
import { getActiveMapsProvider } from '../../../shared/maps';

type HealthStatus = 'up' | 'degraded' | 'down' | 'unknown';

type StatusSource = 'api' | 'statuspage' | 'config';

interface SurfaceCheck {
  name: string;
  status: HealthStatus;
  latency_ms: number | null;
  message: string;
}

interface ServiceHealth {
  service: string;
  status: HealthStatus;
  latency_ms: number | null;
  message: string;
  source: StatusSource;
  checked_at: string;
  // Provider-agnostic Maps row only: which connector is active, and the
  // per-surface breakdown (Maps / Geocoding / Places). Null for other rows.
  provider?: 'google' | 'mapbox' | null;
  checks?: SurfaceCheck[] | null;
}

interface HealthResponse {
  results: ServiceHealth[];
  cache_age_seconds: number;
  cached: boolean;
}

const useStyles = makeStyles({
  pageWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  page: {
    paddingTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    maxWidth: '800px',
    width: '100%',
  },
  toolbarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    ...shorthands.padding('0', '16px'),
  },
  toolbarSpacer: {
    flex: 1,
  },
  card: {
    marginLeft: '16px',
    marginRight: '16px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    ...shorthands.overflow('hidden'),
  },
  rowGroup: {
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    ':first-child': {
      borderTopWidth: 0,
    },
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    ...shorthands.padding('14px', '20px'),
  },
  serviceName: {
    fontWeight: 500,
    fontSize: '14px',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  details: {
    ...shorthands.padding('0', '20px', '14px', '52px'),
    backgroundColor: tokens.colorNeutralBackground2,
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  detailsRow: {
    display: 'flex',
    gap: '8px',
  },
  detailsLabel: {
    color: tokens.colorNeutralForeground3,
    minWidth: '80px',
    flexShrink: 0,
  },
  detailsValue: {
    fontFamily: 'monospace',
    wordBreak: 'break-word',
  },
  surfaces: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginTop: '4px',
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    paddingTop: '8px',
  },
  surfaceRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  surfaceName: {
    fontWeight: 500,
    minWidth: '80px',
    flexShrink: 0,
  },
  surfaceMsg: {
    color: tokens.colorNeutralForeground3,
    flex: 1,
    minWidth: 0,
    wordBreak: 'break-word',
  },
  errorBox: {
    margin: '0 16px',
    ...shorthands.padding('12px'),
    backgroundColor: tokens.colorPaletteRedBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPaletteRedBorder1),
    ...shorthands.borderRadius('4px'),
    color: tokens.colorPaletteRedForeground1,
  },
  loading: {
    ...shorthands.padding('40px'),
    textAlign: 'center',
  },
});

// Public vendor status pages. Linxup has no public status page, so it's
// intentionally absent — that service renders as plain text.
const STATUS_PAGES: Record<string, string> = {
  'Booqable': 'https://status.booqable.com',
  'Authorize.Net': 'https://status.authorize.net',
  'Anthropic': 'https://status.anthropic.com',
  'Supabase (DB)': 'https://status.supabase.com',
  'GitHub': 'https://www.githubstatus.com',
  'Resend': 'https://resend-status.com',
  'Vercel': 'https://www.vercel-status.com',
};

// The Maps umbrella row links to the active connector's status page.
const MAPS_STATUS_PAGE: Record<'google' | 'mapbox', string> = {
  google: 'https://status.cloud.google.com/maps-platform',
  mapbox: 'https://status.mapbox.com',
};

const STATUS_META: Record<HealthStatus, {
  label: string;
  color: string;
  Icon: React.FC<any>;
}> = {
  up: { label: 'Up', color: tokens.colorPaletteGreenForeground1, Icon: CheckmarkCircle20Filled },
  degraded: { label: 'Degraded', color: tokens.colorPalettePumpkinBorderActive, Icon: Warning20Filled },
  down: { label: 'Down', color: tokens.colorPaletteRedForeground1, Icon: ErrorCircle20Filled },
  unknown: { label: 'Unknown', color: tokens.colorNeutralForeground3, Icon: QuestionCircle20Filled },
};

function formatTimestamp(iso: string): string {
  // checked_at is a real event timestamp — render date+time in the business
  // timezone so every viewer reads one frame of reference.
  return formatBusinessDateTime(iso);
}

const SystemHealth: React.FC = () => {
  const styles = useStyles();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Phase 5: maps connector is provider-swappable. Resolve once at mount so
  // the active provider gets badged on its row.
  const activeMapsProvider = getActiveMapsProvider();

  const toggleExpanded = (service: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(service)) next.delete(service);
      else next.add(service);
      return next;
    });
  };

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('setPageContext', {
      detail: { title: 'System Health' },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('setPageContext', { detail: null }));
    };
  }, []);

  const { data, isLoading, error, refetch, isFetching } = useQuery<HealthResponse>({
    queryKey: ['system-health'],
    queryFn: async () => {
      const res = await apiFetch('/system/health');
      if (!res.ok) throw new Error(`Health check failed (${res.status})`);
      return res.json();
    },
    staleTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const handleHardRefresh = () => {
    apiFetch('/system/health?refresh=1').then(() => refetch());
  };

  const results = data?.results ?? [];

  return (
    <div className={styles.pageWrapper}>
    <div className={styles.page}>
      <div className={styles.toolbarRow}>
        <div className={styles.toolbarSpacer} />
        <Button
          icon={isFetching ? <Spinner size="tiny" /> : <ArrowClockwise24Regular />}
          onClick={handleHardRefresh}
          disabled={isFetching}
          appearance="primary"
        >
          {isFetching ? 'Checking…' : 'Refresh'}
        </Button>
      </div>

      {error && (
        <div className={styles.errorBox}>
          {(error as Error).message}
        </div>
      )}

      {isLoading ? (
        <div className={styles.loading}>
          <Spinner label="Probing services…" />
        </div>
      ) : (
        <div className={styles.card}>
          {results.map((r) => {
            const meta = STATUS_META[r.status];
            const Icon = meta.Icon;
            const isMapsRow = r.service === 'Maps';
            // Phase 5: the Maps row is provider-agnostic. Prefer the backend's
            // resolved provider, fall back to the frontend resolver.
            const mapsProvider = isMapsRow ? (r.provider ?? activeMapsProvider) : null;
            const url = isMapsRow
              ? (mapsProvider ? MAPS_STATUS_PAGE[mapsProvider] : undefined)
              : STATUS_PAGES[r.service];
            const sourceLabel =
              r.source === 'api' ? 'API'
              : r.source === 'statuspage' ? 'Page'
              : 'No source';
            const tooltip = `${meta.label} · source: ${sourceLabel}`;
            const isOpen = expanded.has(r.service);
            // Badge the active connector name on the Maps umbrella row.
            const providerBadge = mapsProvider
              ? { label: mapsProvider === 'google' ? 'Google' : 'Mapbox',
                  appearance: 'filled' as const, color: 'brand' as const }
              : null;
            return (
              <div key={r.service} className={styles.rowGroup}>
                <div className={styles.row}>
                  <Tooltip content={tooltip} relationship="label">
                    <Icon style={{ color: meta.color, flexShrink: 0 }} />
                  </Tooltip>
                  {url ? (
                    <Link
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.serviceName}
                    >
                      {r.service}
                    </Link>
                  ) : (
                    <span className={styles.serviceName}>{r.service}</span>
                  )}
                  {providerBadge && (
                    <Badge
                      appearance={providerBadge.appearance}
                      color={providerBadge.color}
                      size="small"
                    >
                      {providerBadge.label}
                    </Badge>
                  )}
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={isOpen ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
                    onClick={() => toggleExpanded(r.service)}
                    aria-label={isOpen ? 'Hide details' : 'Show details'}
                    aria-expanded={isOpen}
                  >
                    Details
                  </Button>
                </div>
                {isOpen && (
                  <div className={styles.details}>
                    <div className={styles.detailsRow}>
                      <span className={styles.detailsLabel}>Source</span>
                      <span>{sourceLabel}</span>
                    </div>
                    <div className={styles.detailsRow}>
                      <span className={styles.detailsLabel}>Latency</span>
                      <span className={styles.detailsValue}>
                        {r.latency_ms == null ? '—' : `${r.latency_ms} ms`}
                      </span>
                    </div>
                    <div className={styles.detailsRow}>
                      <span className={styles.detailsLabel}>Checked</span>
                      <span>{formatTimestamp(r.checked_at)}</span>
                    </div>
                    <div className={styles.detailsRow}>
                      <span className={styles.detailsLabel}>Message</span>
                      <span className={styles.detailsValue}>{r.message || '—'}</span>
                    </div>
                    {r.checks && r.checks.length > 0 && (
                      <div className={styles.surfaces}>
                        {r.checks.map((c) => {
                          const cMeta = STATUS_META[c.status];
                          const CIcon = cMeta.Icon;
                          return (
                            <div key={c.name} className={styles.surfaceRow}>
                              <CIcon style={{ color: cMeta.color, flexShrink: 0, fontSize: '16px' }} />
                              <span className={styles.surfaceName}>{c.name}</span>
                              <span className={styles.detailsValue}>
                                {c.latency_ms == null ? '—' : `${c.latency_ms} ms`}
                              </span>
                              <span className={styles.surfaceMsg}>{c.message || '—'}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
    </div>
  );
};

export default SystemHealth;
